import {
  NodeData,
  MediaData,
  NodeString,
  OptionsResponseAnswer,
  FillInTheBlankKeytakeawayItem,
} from '../../../types/quiz';

/**
 * ParseMainExp.
 */
export const parseMainExp = <T>(exp?: NodeString, mediaData?: MediaData<T>) => {
  if (!exp) {
    return '';
  }

  if (typeof exp === 'string') {
    return exp;
  }

  return parseStringWithNode<T>(exp.s, mediaData as any); // FIXME: any
};

/**
 * GetQuestion.
 */
export const getQuestion = <T>(q: NodeData['q'], mediaData: MediaData<T>) => {
  if (typeof q === 'string') {
    return parseImageNode(q, mediaData);
  }
  return parseStringWithNode<T>(q.s, mediaData);
};

const parseImageNode = <T>(str: string, mediaData: MediaData<T>) => {
  const replaceNode = (input: string): string => {
    const newStr = input.replace(
      /<node id="(\d+)"( src="img:(.+)")? ?\/>/g,
      (a, nodeID, src, foundImgSrc) => {
        const imgSrc = foundImgSrc ?? getSrcFromMedia<T>(+nodeID, mediaData);
        return `<img src="${process.env.NEXT_PUBLIC_S3_BUCKET_URL}/${imgSrc}" />`;
      },
    );

    if (newStr !== input) {
      return replaceNode(newStr);
    }

    return newStr;
  };

  return replaceNode(str) || '';
};

/**
 * ParseStringWithNode.
 */
export function parseStringWithNode<T>(
  nodeString: NodeString,
  mediaData: MediaData<T>,
): string {
  if (!nodeString) return '';
  if (typeof nodeString === 'string') {
    return parseImageNode(nodeString, mediaData);
  }
  return parseImageNode(nodeString.s, mediaData);
}

function getSrcFromMedia<T>(nodeID: number, mediaData: MediaData<T>): string {
  const idx = mediaData.nodeID.indexOf(nodeID);
  if (idx < 0) return '';
  return (mediaData.data as any)[idx]?.$; // FIXME: any
}

function sanitizeHtml(str: string) {
  return str
    ?.replace(/<[^>]*>?/gm, '')
    ?.replace(/&nbsp;|&#160;|&lt;|&gt;/gm, ' ');
}

/**
 * ParseStringWithOptions.
 */
export const parseStringWithOptions = <T>(
  nodeString: NodeString,
  mediaData: MediaData<T>,
): OptionsResponseAnswer[] => {
  const str = typeof nodeString === 'string' ? nodeString : nodeString.s;
  const optionMatches = [...str.matchAll(/\[(.*?)\]/g)];
  const parsedResult: OptionsResponseAnswer[] = [];

  let index = 0;
  let answerIndex = 0;

  if (optionMatches.length) {
    optionMatches.forEach((match, i, arr) => {
      if (match.index) {
        parsedResult.push({
          type: 'text',
          text: str.substring(index, match.index),
        });

        const answer = sanitizeHtml(
          match[1].substring(
            match[1].lastIndexOf('{') + 1,
            match[1].lastIndexOf('}'),
          ),
        );

        const options = sanitizeHtml(match[1].replace(/\{|\}/g, '')).split(',');
        options.forEach((ans, idx) => {
          options[idx] = ans.trim();
        });

        parsedResult.push({
          type: 'select',
          options,
          answer,
          index: answerIndex,
        });

        index = match.index + match[0].length;
        answerIndex += 1;

        if (i === arr.length - 1)
          parsedResult.push({
            type: 'text',
            text: str.substring(index, str.length),
          });
      }
    });
  } else {
    parsedResult.push({
      type: 'text',
      text: str,
    });
  }

  parsedResult.forEach((value) => {
    if (value.type === 'text') {
      value.text = parseImageNode(value.text, mediaData);
    }
  });
  return parsedResult;
};

/**
 * FormStringWithSelects.
 */
export function formStringWithSelects(array: OptionsResponseAnswer[]) {
  let resultString = '';

  for (const value of array) {
    if (value.type === 'text') {
      resultString += value.text;
    } else if (value.type === 'select') {
      resultString += `<select>${value.options.map(
        (option) => `<option>${option}</option>`,
      )}</select>`;
    }
  }
  return resultString;
}

/**
 * ParseKeyTakeaways.
 */
export const parseKeyTakeaways = <T>(
  keyTakeaways: FillInTheBlankKeytakeawayItem[],
  mediaData: MediaData<T>,
): FillInTheBlankKeytakeawayItem[] => {
  return keyTakeaways.map((item) => ({
    ...item,
    takeaway: {
      ...item.takeaway,
      s: parseStringWithNode<T>(item.takeaway.s, mediaData),
    },
  }));
};
